
/*
 * Filename: read-more.js
 * By: J. Nguyen & Stephen Theogene (stheogene@gmail.com)
 * Date: 1/5/22
 *
 * Description: 
   This plugin will power the functionality for the Read More functionality.
   It will detect whether the "read-more-text" content is greater than the 
   default/passed threshold parameter and if so, display the Read More button.
   Once intialized, the plugin will add an "init" class to the container to
   display the visibility.

   2 options are passed via the markup via a data-threshold attribute.  
   If no attribute is passed, default values will be used.

   - data-threshold (int) - pixel value of max text height - default (150px) 
   - data-more-text (str) - overwrite the default read more text - default "Read More"

   threshold and moreText can additionally passed on instantiation to overwrite default
   text if necessary. The data attribute values will overwrite any instantiated values.
   

  DISPLAY MARKUP:
  <div class="read-more {init}" {optional}data-more-text="Read More" data-threshold="150"{/optional} >
    <p class="read-more-text">
      ...
    </p>
    { added/removed dynamically } <span class="read-more-btn">Read More</span> { /added/removed dynamically }
  </div>
 *
 */


export default function ReadMore(element) {

  const elem = element;                       // remember the DOM element that this object is bound to
  let api = {};                               // Object to contain the publicly accessible functions

  // default options
  const defaults = {
    debugMode: false,
    moreText: 'Read More',
    threshold: 150,                           // threshold pertains to $textContainer height, but $elem = threshold + 200px
  };

  // DOM NODES
  const textContainer = elem.getElementsByClassName('read-more-text')[0];
  let moreBtn = null;

  // DATA VARIABLES - private variables
  let moreText = ( elem.dataset.moreText ? elem.dataset.moreText : defaults.moreText );
  let threshold = ( elem.dataset.threshold ? elem.dataset.threshold : defaults.threshold );

  // Template data
  let doc = new DOMParser().parseFromString('<span class="read-more-btn">' + moreText +'</span>', 'text/html');
  let readMoreTag = doc.body.firstChild;

  init();

  // initilize the module
  function init() {
    debug('insider ReadMore init()');

    updateContainers();     // update container threshold if overwritten from default          
    showReadMore()          // detect if height threshold is passed
    displayBlock();
  }


  // PRIVATE HELPER FUNCTIONS
  //===============================================
  
  // Basic debug function
  function debug(msg) { 
    if (defaults.debugMode) {
      console.log(msg);
    }
  }

  // Function will detect if text block is past the height threshold,
  // and return a true/false if the collapse state should be invoked.
  function detectTextHeight() {
    if (textContainer.scrollHeight > threshold) {
      return true;
    } else {
      return false;
    }          
  }

  // This function will expand the content and remove the read more button.
  function expandContent() {
    if (elem.classList.contains('collapsed')) {

      elem.classList.remove('collapsed');
      elem.style.maxHeight = '100%';
      textContainer.style.maxHeight = '100%';

      moreBtn.removeEventListener('click', expandContent);
      moreBtn.remove();

    }  
  }
  
  // PUBLIC FUNCTIONS
  //===============================================
  
  // This function will update the module containers if the threshold
  // default value has been overwritten.  Note that the parent is 200px taller
  // than the text container.
  function updateContainers() {
    const updatedMaxHeight = threshold + 200;

    elem.style.maxHeight = updatedMaxHeight + 'px';
    textContainer.style.maxHeight = threshold + 'px';
  }

  // This function will show the read more button if the content is 
  // taller than the threshold.
  function showReadMore() {

    if ( detectTextHeight() ) {
      // insert read more btn if not already present
      if (!moreBtn) {
        moreBtn = textContainer.parentNode.insertBefore(readMoreTag, textContainer.nextSibling);
        elem.classList.add('collapsed');

        // bind event handler
        moreBtn.addEventListener('click', expandContent);
      }
    } 

  }

  // This function will initialize the read more container by enabling display.
  function displayBlock() {
    elem.classList.add('init');
  }
  
  
  // EXPOSE PUBLIC FUNCTIONS VIA THE API
  // Map all the functions we want to expose for public access
  //===============================================================
  api.updateContainers = updateContainers;
  api.showReadMore = showReadMore;
  api.displayBlock = displayBlock;
  return api;

}
